"use client";
import { motion } from "framer-motion";
import { useLocale } from "next-intl";
import { useRouter } from "next/navigation";
import Link from "next/link";
import React from "react";
import { planFor, planId, TRIAL_DAYS, type Interval, type Tier } from "@/constants/tier";
import { FAIR_USE, TELEPHONY, fmtInt, type FairUseCaps } from "@/constants/fair-use";
import { fmtAmount } from "@/lib/format-price";
import { displayPrices, fillPricingCopy, headlineAmounts, type DisplayPrice } from "@/lib/pricing";
import type { CatalogPricing } from "@/lib/pricing-types";
import { planCookieString } from "@/lib/plan-choice";

// ============================================================================
// Localized content
// ============================================================================

const content = {
  en: {
    eyebrow: "Pricing",
    tagline: "Do it yourself. Or have it done with you.",
    lead:
      "Self-Service: Solo from {solo} per month for one person, Team at {team} per seat per month with everything included — both with a {days}-day free trial, on your own leads from CSV, API or manual entry. Managed: we build and run your complete acquisition system with you, with a partner who works with you every week.",
    selfServiceEyebrow: "Self-Service",
    monthly: "Monthly",
    yearly: "Yearly",
    twoMonthsFree: "2 months free",
    trialNote: "{days}-day free trial · card required · cancel anytime",
    exclVat: "excl. VAT",
    vatFootnote: "Prices for business customers, net. VAT or sales tax is added at checkout where applicable.",
    perMonth: "/ month",
    perSeat: "/ seat / month",
    billedYearly: "billed {total} / year",
    billedYearlySeat: "billed {total} / seat / year",
    billedMonthly: "Billed monthly · cancel anytime",
    solo: {
      name: "Solo",
      tagline: "For one person who calls",
      features: [
        "1 user",
        "Up to 10,000 leads (CSV, API, manual)",
        "Browser VoIP + own SIP or Twilio numbers",
        "Call recording, transcription + AI call documentation",
        "3 active workflows",
        "Custom lists with 66+ filter fields",
        "Email inbox — last 7 days imported on connect",
        "Dynamic scripts, custom fields, dashboards",
      ],
      cta: "Start Solo trial",
    },
    team: {
      name: "Team",
      tagline: "For sales teams of any size",
      badge: "Most popular",
      features: [
        "Unlimited users — roles, permissions & team management",
        "Up to 1,000,000 leads (CSV, API, manual)",
        "Browser VoIP + own SIP or Twilio numbers",
        "Call recording, transcription + AI call documentation",
        "Unlimited workflows: emails, todos, appointments, webhooks",
        "Custom lists with 66+ filter fields",
        "Shared inboxes — 12 months of email imported on connect",
        "REST API + signed webhooks, Google Calendar, IMAP/SMTP",
      ],
      cta: "Start Team trial",
    },
    telephonyNote: `Telephony is pay-as-you-go on both plans: carrier rate + ${TELEPHONY.serviceFeePct} % service fee (plus a ${TELEPHONY.fxBufferPct} % currency buffer) from a prepaid balance, €${TELEPHONY.starterCreditEur} starting credit included, top-ups from €${TELEPHONY.minTopupEur}. All prices excl. VAT.`,
    included: {
      title: "Included every month",
      subtitle: "Fair-use limits for the AI and audio features — they never block a call. Only an empty call balance pauses outbound calls.",
      metricDocs: "AI call documentations",
      metricTranscription: "Transcription minutes",
      metricRecording: "Recording minutes",
      colSolo: "Solo",
      colTeam: "Team, per seat",
      colTrial: "During the trial",
      note: `You get a notice at ${FAIR_USE.warnPct} % and at the limit. At the limit, documentation and transcription pause until the next month.`,
      termsLink: "Fair use in the Terms",
    },
    onboardingBadge: "Optional add-on",
    onboardingTitle: "Onboarding package",
    onboardingPrice: "€1,990 one-time, excl. VAT",
    onboardingDesc:
      "Only if you want it: we set up your CRM with you — lists, custom fields, dashboards, scripts and the first workflows. Self-Service works fully without it.",
    managedEyebrow: "Managed",
    managedTagline: "Your complete acquisition system. Built, run and improved — with you.",
    managedPrice: "€3,500",
    managedPriceSub: "/ month, excl. VAT",
    managedSeats: "10 seats included · additional seats €99 / seat / month",
    managedSetup: "+ €5,000 one-time setup — 50% at kickoff, 50% at go-live",
    managedTerms: "12-month minimum term · scope defined in a separate project contract · all prices excl. VAT",
    managedFeatures: [
      "Everything in Team",
      "Full project setup with you: lists, fields, scripts, dashboards",
      "Custom lead-gen crawler built for your sources and markets",
      "AI enrichment pipeline tuned to your ideal customer profile",
      "Crawler and pipeline maintained and developed further for you",
      "All workflows and automations built for you",
      "Dedicated account manager",
      "WhatsApp support in business hours, reply within 4h",
      "Weekly working call + quarterly review",
    ],
    managedCta: "Book a call",
    managedMore: "How Managed works",
    managedNote:
      "For teams that don't want to buy software but a working acquisition engine: lead sources, enrichment, calling processes, automations and dashboards — set up with you, operated with you, developed further with you.",
  },
  de: {
    eyebrow: "Pricing",
    tagline: "Selbst machen. Oder mit uns machen lassen.",
    lead:
      "Self-Service: Solo ab {solo} im Monat für eine Person, Team für {team} pro Seat und Monat mit allem drin — beide {days} Tage kostenlos testen, mit deinen eigenen Leads aus CSV, API oder manueller Eingabe. Managed: Wir bauen und betreiben dein komplettes Akquise-System mit dir, mit einem Partner, der jede Woche mitarbeitet.",
    selfServiceEyebrow: "Self-Service",
    monthly: "Monatlich",
    yearly: "Jährlich",
    twoMonthsFree: "2 Monate gratis",
    trialNote: "{days} Tage kostenlos testen · Karte erforderlich · jederzeit kündbar",
    exclVat: "zzgl. MwSt.",
    vatFootnote: "Preise für Geschäftskunden, netto. Die Umsatzsteuer wird im Checkout ergänzt, sofern sie anfällt.",
    perMonth: "/ Monat",
    perSeat: "/ Seat / Monat",
    billedYearly: "{total} / Jahr abgerechnet",
    billedYearlySeat: "{total} / Seat / Jahr abgerechnet",
    billedMonthly: "Monatliche Abrechnung · jederzeit kündbar",
    solo: {
      name: "Solo",
      tagline: "Für eine Person, die telefoniert",
      features: [
        "1 Nutzer",
        "Bis zu 10.000 Leads (CSV, API, manuell)",
        "Browser-VoIP + eigene SIP- oder Twilio-Nummern",
        "Aufnahme, Transkription + AI-Anrufdokumentation",
        "3 aktive Workflows",
        "Custom Lists mit 66+ Filter-Feldern",
        "E-Mail-Postfach — die letzten 7 Tage werden beim Verbinden importiert",
        "Dynamic Scripts, Custom Fields, Dashboards",
      ],
      cta: "Solo kostenlos testen",
    },
    team: {
      name: "Team",
      tagline: "Für Vertriebsteams jeder Größe",
      badge: "Beliebt",
      features: [
        "Unbegrenzt Nutzer — Rollen, Berechtigungen & Team-Verwaltung",
        "Bis zu 1.000.000 Leads (CSV, API, manuell)",
        "Browser-VoIP + eigene SIP- oder Twilio-Nummern",
        "Aufnahme, Transkription + AI-Anrufdokumentation",
        "Unbegrenzt Workflows: Mails, Aufgaben, Termine, Webhooks",
        "Custom Lists mit 66+ Filter-Feldern",
        "Shared Inboxes — 12 Monate E-Mails werden beim Verbinden importiert",
        "REST-API + signierte Webhooks, Google Calendar, IMAP/SMTP",
      ],
      cta: "Team kostenlos testen",
    },
    telephonyNote: `Telefonie ist auf beiden Plänen Pay-as-you-go: Netzbetreiberpreis + ${TELEPHONY.serviceFeePct} % Servicegebühr (zzgl. ${TELEPHONY.fxBufferPct} % Währungspuffer) vom Prepaid-Guthaben, ${TELEPHONY.starterCreditEur} € Startguthaben inklusive, Aufladungen ab ${TELEPHONY.minTopupEur} €. Alle Preise zzgl. MwSt.`,
    included: {
      title: "Jeden Monat inklusive",
      subtitle: "Fair-Use-Grenzen für die AI- und Audio-Funktionen – sie blockieren nie einen Anruf. Nur ein leeres Gesprächsguthaben pausiert ausgehende Anrufe.",
      metricDocs: "AI-Anrufdokumentationen",
      metricTranscription: "Transkriptionsminuten",
      metricRecording: "Aufnahmeminuten",
      colSolo: "Solo",
      colTeam: "Team, pro Seat",
      colTrial: "In der Testphase",
      note: `Du bekommst einen Hinweis bei ${FAIR_USE.warnPct} % und beim Erreichen der Grenze. An der Grenze pausieren Dokumentation und Transkription bis zum nächsten Monat.`,
      termsLink: "Fair Use in den Nutzungsbedingungen",
    },
    onboardingBadge: "Optionales Add-on",
    onboardingTitle: "Onboarding-Paket",
    onboardingPrice: "1.990 € einmalig, zzgl. MwSt.",
    onboardingDesc:
      "Nur wenn du willst: Wir richten dein CRM mit dir ein — Listen, Custom Fields, Dashboards, Skripte und die ersten Workflows. Self-Service funktioniert auch komplett ohne.",
    managedEyebrow: "Managed",
    managedTagline: "Dein komplettes Akquise-System. Gebaut, betrieben und weiterentwickelt — mit dir.",
    managedPrice: "3.500 €",
    managedPriceSub: "/ Monat, zzgl. MwSt.",
    managedSeats: "10 Seats inklusive · weitere Seats 99 € / Seat / Monat",
    managedSetup: "+ 5.000 € einmalig Setup — 50 % bei Kickoff, 50 % bei Go-live",
    managedTerms: "12 Monate Mindestlaufzeit · Leistungsumfang im separaten Projektvertrag · alle Preise zzgl. MwSt.",
    managedFeatures: [
      "Alles aus Team",
      "Kompletter Projektaufbau mit dir: Listen, Felder, Skripte, Dashboards",
      "Eigener Lead-Gen-Crawler für deine Quellen und Märkte",
      "AI-Enricher-Pipeline abgestimmt auf dein Wunschkundenprofil",
      "Crawler und Pipeline werden für dich gewartet und weiterentwickelt",
      "Alle Workflows und Automatisierungen für dich gebaut",
      "Dedizierter Account Manager",
      "WhatsApp-Support zu Geschäftszeiten, Antwort innerhalb von 4 h",
      "Wöchentlicher Arbeits-Call + Quartals-Review",
    ],
    managedCta: "Gespräch vereinbaren",
    managedMore: "So funktioniert Managed",
    managedNote:
      "Für Teams, die keine Software kaufen wollen, sondern ein funktionierendes Akquise-System: Lead-Quellen, Anreicherung, Anrufprozesse, Automatisierungen und Dashboards — mit dir aufgesetzt, mit dir betrieben, mit dir weiterentwickelt.",
  },
};

type ContentDe = typeof content.de;
type ContentEn = typeof content.en;
type Content = ContentDe | ContentEn;

type Prices = Record<Tier, Record<Interval, DisplayPrice>>;

// ============================================================================
// Root
// ============================================================================

/**
 * `pricing` = the live catalog from the backend (server component passes it;
 * lib/pricing.ts). Without it the static fallback amounts from constants/tier
 * render, so the section never breaks when the backend is unreachable.
 */
export function Pricing({ pricing = null, trialDays = TRIAL_DAYS }: { pricing?: CatalogPricing | null; trialDays?: number }) {
  const locale = useLocale();
  const router = useRouter();
  const [interval, setInterval] = React.useState<Interval>("monthly");
  const prices = React.useMemo(() => displayPrices(pricing), [pricing]);
  const c = React.useMemo(() => {
    const base = locale === "de" ? content.de : content.en;
    const vars = { ...headlineAmounts(pricing, locale), days: trialDays };
    return { ...base, lead: fillPricingCopy(base.lead, vars), trialNote: fillPricingCopy(base.trialNote, vars) };
  }, [locale, pricing, trialDays]);

  const handleStart = (tier: Tier) => {
    const id = planId(tier, interval);
    // Remember the pick for the hand-offs that lose the URL parameter
    // (Google sign-in, magic link in another tab, "log in instead").
    try {
      document.cookie = planCookieString(id, window.location.protocol === "https:");
    } catch {
      /* cookies blocked — the URL parameter still carries it */
    }
    router.push(`/signup?plan=${id}`);
  };

  return (
    <section
      id="pricing"
      className="dark relative w-full overflow-hidden bg-neutral-950 scroll-mt-24"
    >
      <SectionBackground />
      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-20 md:py-28 lg:py-32">
        <BrandReveal c={c} />

        {/* Self-service: interval toggle + Solo / Team */}
        <div className="mt-10 flex flex-col items-center gap-3">
          <span className="text-[10px] uppercase tracking-widest font-semibold text-red-300">
            {c.selfServiceEyebrow}
          </span>
          <IntervalToggle c={c} interval={interval} onChange={setInterval} />
          <p className="text-xs text-neutral-500">{c.trialNote}</p>
          <p className="text-xs text-neutral-500">{c.vatFootnote}</p>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          <div className="lg:col-span-5">
            <TierCard c={c} prices={prices} tier="solo" interval={interval} locale={locale} onStart={() => handleStart("solo")} />
          </div>
          <div className="lg:col-span-7">
            <TierCard c={c} prices={prices} tier="team" interval={interval} locale={locale} onStart={() => handleStart("team")} highlighted />
          </div>
        </div>

        <IncludedBlock c={c} locale={locale} />

        <p className="mt-4 text-center text-xs text-neutral-500">{c.telephonyNote}</p>

        {/* Managed card */}
        <div className="mt-6">
          <ManagedCard c={c} locale={locale} />
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Background
// ============================================================================

const SectionBackground = () => (
  <>
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_30%,rgba(239,68,68,0.18),transparent_65%)]" />
    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-red-500/40 to-transparent" />
    <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-red-500/40 to-transparent" />
    <EmberParticles />
  </>
);

const EmberParticles = () => {
  const seeds = React.useMemo(
    () =>
      Array.from({ length: 22 }).map((_, i) => ({
        left: (i * 41) % 100,
        size: 1 + (i % 3) * 0.5,
        duration: 7 + (i % 5) * 1.3,
        delay: (i * 0.37) % 8,
      })),
    []
  );
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {seeds.map((s, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full bg-red-400"
          style={{
            left: `${s.left}%`,
            bottom: 0,
            width: `${s.size}px`,
            height: `${s.size}px`,
            boxShadow: "0 0 6px 1px rgba(239,68,68,0.7)",
          }}
          initial={{ y: 0, opacity: 0 }}
          animate={{
            y: [-20, -600 - (i % 4) * 60],
            opacity: [0, 0.9, 0.9, 0],
            x: [0, (i % 2 === 0 ? 1 : -1) * (10 + (i % 3) * 6)],
          }}
          transition={{
            repeat: Infinity,
            duration: s.duration,
            delay: s.delay,
            ease: "linear",
            times: [0, 0.15, 0.75, 1],
          }}
        />
      ))}
    </div>
  );
};

// ============================================================================
// Brand Reveal
// ============================================================================

const BrandReveal = ({ c }: { c: Content }) => (
  <div className="flex flex-col items-center text-center">
    <div className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-3 py-1 text-[11px] font-medium uppercase tracking-widest text-red-300">
      <FlameIcon className="h-3 w-3 text-red-400" />
      <span>{c.eyebrow}</span>
    </div>
    <h2 className="mt-6 font-bold leading-tight tracking-tight text-3xl md:text-5xl text-white max-w-3xl">
      {c.tagline}
    </h2>
    <p className="mt-5 text-sm md:text-base text-neutral-400 max-w-2xl leading-relaxed">
      {c.lead}
    </p>
  </div>
);

// ============================================================================
// Interval toggle
// ============================================================================

const IntervalToggle = ({
  c,
  interval,
  onChange,
}: {
  c: Content;
  interval: Interval;
  onChange: (i: Interval) => void;
}) => (
  <div
    role="radiogroup"
    className="inline-flex items-center rounded-full border border-neutral-800 bg-neutral-900/70 p-1"
  >
    {(["monthly", "yearly"] as Interval[]).map((iv) => (
      <button
        key={iv}
        type="button"
        role="radio"
        aria-checked={interval === iv}
        onClick={() => onChange(iv)}
        className={
          "rounded-full px-4 py-1.5 text-sm font-medium transition-colors " +
          (interval === iv ? "bg-white text-black" : "text-neutral-400 hover:text-white")
        }
      >
        {c[iv]}
        {iv === "yearly" && (
          <span
            className={
              "ml-2 rounded-full px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold " +
              (interval === iv ? "bg-red-500/15 text-red-600" : "bg-red-500/15 text-red-300")
            }
          >
            {c.twoMonthsFree}
          </span>
        )}
      </button>
    ))}
  </div>
);

// ============================================================================
// Self-service tier card (Solo / Team)
// ============================================================================

const TierCard = ({
  c,
  prices,
  tier,
  interval,
  locale,
  onStart,
  highlighted = false,
}: {
  c: Content;
  prices: Prices;
  tier: Tier;
  interval: Interval;
  locale: string;
  onStart: () => void;
  highlighted?: boolean;
}) => {
  const tc = c[tier];
  const price = prices[tier][interval];
  const plan = planFor(tier, interval);
  const billedNote =
    interval === "yearly"
      ? (tier === "team" ? c.billedYearlySeat : c.billedYearly).replace("{total}", fmtAmount(price.total, price.currency, locale))
      : c.billedMonthly;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={
        "relative h-full rounded-3xl overflow-hidden border flex flex-col " +
        (highlighted
          ? "border-red-500/40 bg-gradient-to-br from-red-950/40 via-neutral-900 to-neutral-950 shadow-[0_0_60px_rgba(239,68,68,0.18)]"
          : "border-neutral-800 bg-neutral-900/60")
      }
      data-plan={plan.id}
    >
      {highlighted && (
        <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-red-500/50 to-transparent" />
      )}

      <div className="p-7 md:p-9 flex flex-col flex-1">
        <div className="flex items-baseline justify-between">
          <span className="text-[10px] uppercase tracking-widest font-semibold text-red-300">
            {tc.name}
          </span>
          {"badge" in tc && tc.badge && (
            <span className="inline-flex items-center gap-1 rounded-full text-[10px] uppercase tracking-wider font-semibold text-red-200 bg-red-500/15 ring-1 ring-red-500/30 px-2 py-0.5">
              {tc.badge}
            </span>
          )}
        </div>
        <p className="mt-2 text-base text-neutral-400">{tc.tagline}</p>

        <motion.div
          key={`${tier}-${interval}`}
          initial={{ opacity: 0.4, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mt-7 flex items-baseline gap-2"
        >
          <span
            className={
              "font-extrabold tabular-nums " +
              (highlighted
                ? "text-6xl md:text-7xl bg-clip-text text-transparent bg-gradient-to-br from-red-300 via-red-500 to-red-600 drop-shadow-[0_0_30px_rgba(239,68,68,0.35)]"
                : "text-5xl md:text-6xl text-white")
            }
          >
            {fmtAmount(price.perMonth, price.currency, locale)}
          </span>
          <span className="text-sm font-medium text-neutral-400">
            {tier === "team" ? c.perSeat : c.perMonth}
          </span>
        </motion.div>
        <p className="mt-2 text-xs text-neutral-500">
          {billedNote} · {c.exclVat}
        </p>

        <button
          type="button"
          onClick={onStart}
          className={
            "mt-7 w-full rounded-full font-semibold text-sm py-3 transition-colors " +
            (highlighted
              ? "bg-white text-black hover:bg-neutral-100 shadow-lg shadow-red-500/10"
              : "border border-neutral-700 bg-neutral-900 text-white hover:bg-neutral-800")
          }
        >
          {tc.cta}
        </button>

        <ul className="mt-7 grid grid-cols-1 gap-y-3">
          {tc.features.map((feature) => (
            <li
              key={feature}
              className="flex items-start gap-2.5 text-sm text-neutral-200 leading-relaxed"
            >
              <CheckIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-red-400" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        {/* Onboarding add-on (Team only) */}
        {tier === "team" && (
          <div className="mt-auto pt-6">
            <div className="rounded-xl border border-dashed border-neutral-700 bg-neutral-900/40 p-4">
              <span className="inline-flex items-center rounded-full border border-neutral-700 bg-neutral-900 px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold text-neutral-400">
                {c.onboardingBadge}
              </span>
              <div className="mt-2 flex items-baseline justify-between gap-3">
                <span className="text-[11px] font-semibold text-white">
                  {c.onboardingTitle}
                </span>
                <span className="text-[11px] font-mono text-red-300 whitespace-nowrap">
                  {c.onboardingPrice}
                </span>
              </div>
              <p className="mt-2 text-[11px] text-neutral-500 leading-relaxed">
                {c.onboardingDesc}
              </p>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ============================================================================
// Included per month (fair use)
// ============================================================================

const IncludedBlock = ({ c, locale }: { c: Content; locale: string }) => {
  const rows: { label: string; key: keyof FairUseCaps }[] = [
    { label: c.included.metricDocs, key: "callDocs" },
    { label: c.included.metricTranscription, key: "transcriptionMin" },
    { label: c.included.metricRecording, key: "recordingMin" },
  ];
  const cols: { label: string; caps: FairUseCaps }[] = [
    { label: c.included.colSolo, caps: FAIR_USE.solo },
    { label: c.included.colTeam, caps: FAIR_USE.teamPerSeat },
    { label: c.included.colTrial, caps: FAIR_USE.trial },
  ];
  return (
    <div id="included" className="mt-6 rounded-2xl border border-neutral-800 bg-neutral-900/60 p-6 md:p-8 scroll-mt-24">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-white">{c.included.title}</h3>
          <p className="mt-1 text-xs text-neutral-500 max-w-xl">{c.included.subtitle}</p>
        </div>
        <Link
          href={`/${locale}/terms#fair-use`}
          className="text-xs font-semibold text-neutral-300 hover:text-white transition-colors whitespace-nowrap"
        >
          {c.included.termsLink} <span aria-hidden>→</span>
        </Link>
      </div>
      <div className="mt-5 overflow-x-auto">
        <table className="w-full min-w-[480px] text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500">
              <th className="py-2 pr-4 font-semibold" />
              {cols.map((col) => (
                <th key={col.label} className="py-2 px-3 font-semibold text-right">{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-t border-neutral-800">
                <td className="py-2.5 pr-4 text-neutral-300">{row.label}</td>
                {cols.map((col) => (
                  <td key={col.label} className="py-2.5 px-3 text-right font-mono tabular-nums text-neutral-100">
                    {fmtInt(col.caps[row.key], locale)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-4 text-xs text-neutral-500">{c.included.note}</p>
    </div>
  );
};

// ============================================================================
// Managed card
// ============================================================================

const ManagedCard = ({ c, locale }: { c: Content; locale: string }) => (
  <div className="rounded-2xl border border-neutral-800 bg-neutral-900/60 p-6 md:p-8 flex flex-col md:flex-row md:items-start gap-6">
    <div className="flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-red-300 mb-2">
        {c.managedEyebrow}
      </div>
      <h3 className="text-xl md:text-2xl font-bold text-white tracking-tight">
        {c.managedTagline}
      </h3>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="text-3xl font-extrabold text-white tabular-nums">
          {c.managedPrice}
        </span>
        <span className="text-sm text-neutral-400">{c.managedPriceSub}</span>
      </div>
      <p className="text-xs text-neutral-300 mt-2">{c.managedSeats}</p>
      <p className="text-xs text-neutral-500 mt-1">{c.managedSetup}</p>
      <p className="text-xs text-neutral-500 mt-1">{c.managedTerms}</p>
      <p className="text-xs text-neutral-500 mt-3">{c.managedNote}</p>
    </div>

    <div className="flex-1 min-w-0 md:border-l md:border-neutral-800 md:pl-6">
      <ul className="grid grid-cols-1 gap-y-2 mb-5">
        {c.managedFeatures.map((feature) => (
          <li
            key={feature}
            className="flex items-start gap-2 text-[13px] text-neutral-300"
          >
            <CheckIcon className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-red-400" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <Link
          href={`/${locale}/contact`}
          className="inline-flex items-center justify-center w-full sm:w-auto rounded-full border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 text-red-200 font-semibold text-sm px-5 py-2.5 transition-colors"
        >
          {c.managedCta}
        </Link>
        <Link
          href={`/${locale}/managed`}
          className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-neutral-300 hover:text-white transition-colors"
        >
          {c.managedMore}
          <span aria-hidden>→</span>
        </Link>
      </div>
    </div>
  </div>
);

// ============================================================================
// Icons
// ============================================================================

function FlameIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M12 2.5c1.3 3.1 3.8 4.6 4.7 6.9.9 2.3.3 4.7-1.4 6.4-.6.6-1.3 1-2.1 1.2.5-.9.6-2 .2-3-.5-1.2-1.7-1.9-2.9-1.9-.3 1-.9 1.9-1.7 2.5-1.7 1.3-2.3 3.5-1.6 5.4.5 1.4 1.6 2.6 3 3-3.7-.2-7-3.1-7.4-6.9-.4-4.1 2.4-7.7 5.5-10.1C10.3 4.7 11.4 3.6 12 2.5z" />
    </svg>
  );
}


function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M4 12l5 5L20 6" />
    </svg>
  );
}
